/**
 * Transforms a `button` + block-level element into a 'disclosure'.
 *
 * Prefers, a `button.disclosure__toggle` element followed immediately by any
 * block level element, but can accommodate any combination and order of
 * elements.
 *
 * Aids in making the resulting disclosure elements accessible, but does not
 * guarantee that -- the author of the markup and CSS has to take ultimate
 * responsibility for that. This class just aims to do the interactivity part
 * without making things worse.
 *
 * Similarly, this class should work well with progressive enhancement since
 * it does very little manipulation to the DOM, and expects the element that
 * contains the 'disclosed' information to be visible by default, and the
 * button that triggers the 'disclosure' to be hidden by default.
 *
 * The class exposes a small public api and customised events allowing external
 * scripts to do the following:
 *
 *   - discover the enabled state (via disclosure.isEnabled()),
 *   - discover the expanded state (via disclosure.isExpanded()),
 *   - enable the disclosure (via disclosure.enable()),
 *   - disable the disclosure (via disclosure.disable()),
 *   - expand the disclosure (via disclosure.expand()),
 *   - collapse the disclosure (via disclosure.collapse()),
 *   - listen for disclosure events on 'enable', 'disable', 'expand', and
 *     collapse (this action is contained in the event.detail.action property
 *     of 'disclosure' events).
 *
 * Class methods aside from constructor() ordered alphabetically.
 *
 * @see {@link https://www.w3.org/WAI/ARIA/apg/|W3C's ARIA Authoring Practices Guide (APG)}
 * @see {@link https://www.w3.org/WAI/ARIA/apg/patterns/disclosure/|ARIA disclosure pattern}
 * @see {@link https://annertech.pages.anner.ie/modules/disclosure/|Demo}
 * @example
 * // Creating a new disclosure in a Drupal behavior.
 * (function disclosureScript(Drupal) {
 *   Drupal.behaviors.befFieldsetDisclosure = {
 *     attach(context) {
 *       const disclosureToggles = once(
 *         'disclosuretoggle', '.disclosure__toggle', context
 *       );
 *       const disclosures = [];
 *
 *       if (!disclosureToggles) {
 *         return;
 *       }
 *
 *       disclosureToggles.forEach((toggle) => {
 *         disclosures.push(new Disclosure(toggle));
 *       });
 *     },
 *   };
 * }(Drupal));
 * @example
 * <!-- Sample markup -->
 * <button aria-expanded="true" hidden class="disclosure__toggle" hidden>
 *   Show contents
 * </button>
 * <div class="disclosure__contents" data-disclosure-expanded="show">
 *   <p>Lorem ipsum dolor sit amet consectetuer.</p>
 * </div>
 *
 * @class
 */
class Disclosure {

  /**
   * @property {boolean} _defaultState
   *   Whether or not the default condition of the disclosure is expanded.
   * @protected
   */
  _defaultState;

  /**
   * @property {object} _button
   *   The button used to trigger the disclosure.
   * @protected
   */
  _button;

  /**
   * @property {object} _contents
   *   The contents disclosed by the button.
   * @protected
   */
  _contents;

  /**
   * @property {boolean} _enabled
   *   Whether or not the disclosure is currently enabled.
   * @protected
   */
  _enabled = false;

  /**
   * @property {boolean} _expanded
   *   Whether or not the disclosure is currently expanded.
   * @protected
   */
  _expanded;

  /**
   * @property {string} _id
   *   A reasonably unique id for this disclosure.
   * @protected
   */
  _id;

  /**
   * @property {string} _name
   *   The name of this object. Used in custom event types.
   * @protected
   */
  _name = 'disclosure';

  /**
   * @property {array} _plugins
   *   The set of plugins to use on this instantation of the object.
   * @protected
   */
  _plugins = [];

  /**
   * @property {object} _settings
   *   Settings for the running Disclosure.
   * @see defaults
   * @protected
   */
  _settings;

  /**
   * @property {object} defaults
   *   Default settings values.
   * @property {boolean} defaults.autoStart
   *   Whether or not the object should be configured to autoStart.
   * @public
   */
  defaults = { autoStart: true, validateAriaControls: true };

  /**
   * Constructs a new Disclosure object.
   *
   * @param {HTMLElement} button
   *   The DOM element used to show and hide the disclosure contents.
   * @param {HTMLElement|array|object|undefined} contents
   *   The DOM element, if any, that 'button' should show and hide (we prefer
   *   to rely on the two elements being siblings, with 'contents' following
   *   immediately after 'button', but if that's not possible we use this
   *   parameter). May also be an array or NodeList of suitable objects for
   *   situations where `aria-controls` is multiple.
   * @param {object} settings
   *   Configuration values for the Disclosure object.
   * @param {boolean} settings.autoStart
   *   Whether or not to automatically enable the disclosure on instantiation.
   * @param {boolean} settings.validateAriaControls
   *   Whether or not to validate that aria-controls attribute is present and
   *   contents elements have appropriate id attributes.
   * @param {array} plugins
   *   An array of plugins to run on the Disclosure's init, enable, or expand
   *   hooks.
   * @constructor
   */
  constructor(button, contents = undefined, settings = {}, plugins = []) {
    // Set up class properties.
    this._button = button;
    this._contents = this.#setContents(contents);
    this._settings = { ...this.defaults, ...settings };
    this._plugins = plugins.map((plugin) => this.#registerPlugin(plugin));
    this._defaultState = this._setDefaultState();
    this._id = this.#setId();

    // Get it going.
    this.#init();
  }

  /**
   * Enables the disclosure.
   *
   * Sets various DOM element attributes to their default states, updates the
   * ojbect's enabled property, runs 'enable' plugins, and dispatches an event
   * when complete.
   *
   * @param {boolean} enable
   *   Whether to enable or disable the Disclosure.
   * @fires Disclosure#enable
   * @fires Disclosure#disable
   * @see {@link #runPlugin}
   * @see {@link _getEvent}
   * @see {@link disable}
   * @see {@link expand}
   * @function
   * @public
   */
  #control = (enable = true) => {
    enable
      ? this._button.addEventListener('click', this._handleButtonClick)
      : this._button.removeEventListener('click', this._handleButtonClick);
    this.#toggle(enable ? this._defaultState : false);
    this._button.hidden = !enable;
    this._enabled = enable;
    this._plugins.forEach((plugin) =>
      (plugin.hook === 'enable') ? this.#runPlugin(plugin) : null);
    this._button.dispatchEvent(this._getEvent(enable ? 'enable' : 'disable'));
  };

  /**
   * Transforms incoming contents parameter into an iterable set of elements.
   *
   * The function tests that param is truthy:
   *
   *   - if it is and if it's iterable, we return it directly,
   *   - if it is and it's not iterable, we return it in an array,
   *   - if it's not truthy, we return the next sibling to this._button in an
   *     array.
   *
   * @param {object|null} param
   *   The incoming contents value.
   * @return {array|NodeList}
   *   The resulting array or NodeList of one or more toggle containers.
   * @function
   * @private
   */
  #setContents = (param) => param
    ? (typeof param[Symbol.iterator] === 'function' ? param : [param])
    : [this._button.nextElementSibling];

  /**
   * Returns the set of public methods that plugins can make use of.
   *
   * @return {object}
   *   The set of public methods usable by plugins. Passed to a plugin as its
   *   'methods' parameter.
   * @see {@link #runPlugin}
   * @function
   * @private
   */
  #getPluginMethods = () => ({
    collapse: this.collapse,
    disable: this.disable,
    enable: this.enable,
    expand: this.expand,
    isEnabled: this.isEnabled,
    isExpanded: this.isExpanded,
  });

  /**
   * Initializes class interactivity.
   *
   * @function
   * @private
   */
  #init = () => {
    try {
      // We add an identifier to this._button before we do *anything* else so
      // that debugging can be made simpler if this.#validateAriaControls()
      // throws an error.
      this._button.dataset.disclosureId = this._id;
      // Validate aria-controls attributes and their referents.
      this._settings.validateAriaControls ? this.#validateAriaControls() : null;
      // Autostart if required, run init-hook plugins, and add the object to
      // the DOM element corresponding to this._button.
      this._settings.autoStart ? this.enable() : null;
      this._plugins.forEach((plugin) =>
        (plugin.hook === 'init') ? this.#runPlugin(plugin) : null);
      this._button.disclosure = this;
    } catch (error) {
      console.error(error.message);
    }
  }

  /**
   * Validates and registers plugins provided by the caller.
   *
   * @param {object} plugin
   *   A plugin designed to be run on this.#init(), this.collapse(), or on
   *   this.expand().
   * @param {object} plugin.args
   *   An arbitrary object of settings to be passed to the plugin's callback
   *   function.
   * @param {object} plugin.callback
   *   A function to call on one of the object's hooks. Will have access to
   *   plugin.args (as its 'args' parameter), and to the class's public methods
   *   (as its 'methods' parameter).
   * @param {string} plugin.hook
   *   The name of the hook to call the function on. Must be one of 'enable',
   *   'expand', or 'init'. In the case of the 'enable' and 'expand' hooks, the
   *   plugin callback will be called regardless of whether the current action
   *   is expand, or collapse (when set to 'expand'), or enable or disable (when
   *   set to 'enable'), so the callback should use isEnabled() or isExpanded()
   *   as required.
   * @param {string} plugin.name
   *   The name of the plugin. Should be unique.
   * @return {object} plugin
   *   The incoming plugin, provided it didn't fail validation.
   * @function
   * @private
   * @see {@link #runPlugin}
   */
  #registerPlugin = (plugin) => {
    const hooks = ['enable', 'expand', 'init'];
    let duplicatePlugins;

    // Check that the plugin has a defined name.
    if (!plugin.hasOwnProperty('name') || plugin.name.trim() === '') {
      throw `Each plugin must include a non-empty "name" property.`;
    }

    // Check that the plugin tries to attach itself to a hook that exists.
    if (!plugin.hasOwnProperty('hook') || !hooks.includes(plugin.hook)) {
      throw `The "hook" property of the plugin "${plugin.name}" must be set to one of the following strings: ${hooks.join(', ')}.`;
    }

    // Check that the plugin callback is a function.
    if (
      !plugin.hasOwnProperty('callback')
      || !(plugin.callback instanceof Function)
    ) {
      throw `The "callback" property of the plugin "${plugin.name}" is not an instance of Function.`;
    }

    return plugin;
  };

  /**
   * Runs a given plugin.
   *
   * Runs plugin's callback function, passing it plugin.args, and this class's
   * public methods.
   *
   * @param {object} plugin
   *   The plugin to execute.
   * @function
   * @private
   * @see {@link #getPluginMethods}
   * @see {@link #registerPlugin}
   */
  #runPlugin = (plugin) =>
    plugin.callback((plugin.args || {}), this.#getPluginMethods());

  /**
   * Returns an EPHEMERAL and NON-CRYPTOGRAPHICALLY-SAFE hex id from 0 to max.
   *
   * The value does not persist between instantiations, so will be different
   * each time the disclosure reloads. Its main use is to identify WHICH of
   * multiple disclosures that might be present has thrown an exception.
   *
   * @param {number} max
   *   The maximum random value for the function to generate.
   * @return {string}
   *   The hexadecimal representation of the generated pseudorandom number.
   *   given the default value for max (100000), will return a string of < 6
   *   characters.
   */
  #setId = (max = 100000) => Math.floor(Math.random() * max).toString(16);

  /**
   * Handles expanding and collapsing the disclosure.
   *
   * Updates markup, object property _expanded, and dispatches 'disclosure'
   * event with event.detail.action set to 'expand' or 'collapse'
   *
   * @param {boolean} expand
   *   Whether to expand (true) or collapse (false) the disclosure.
   * @fires Disclosure#collapse
   * @fires Disclosure#expand
   * @see {@link collapse}
   * @function
   * @public
   */
  #toggle = (expand = true) => {
    this._button.setAttribute('aria-expanded', expand);
    this._contents.forEach((content) =>
      content.setAttribute('data-disclosure-expanded', expand));
    this._expanded = expand;
    this._plugins.forEach((plugin) =>
      (plugin.hook === 'expand') ? this.#runPlugin(plugin) : null);
    this._button.dispatchEvent(this._getEvent(expand ? 'expand' : 'collapse'));
  };

  /**
   * Validates aria-controls attribute and corresponding id attributes.
   *
   * Specifically, verifies that:
   *
   *   - this._button *has* an aria-controls attribute,
   *   - this._button's aria-controls attribute is not empty,
   *   - each of this._contents *has* an id attribute,
   *   - each of this._contents' id attributes is not empty,
   *   - each of this._contents' id attributes is included in this._button's
   *     aria-controls attribute.
   *
   * We don't go out of our way to test this._button's aria-expanded attribute
   * because the whole point of Disclosure.js is to control that attribute in
   * the first place.
   *
   * @todo Should this be a plugin?
   */
  #validateAriaControls = () => {
    const ariaControlsExists = this._button.hasAttribute('aria-controls');
    const ariaControlsValue = ariaControlsExists ? this._button.getAttribute('aria-controls') : '';
    let ids;

    // Verify that the aria-controls attr exists.
    if (!ariaControlsExists) {
      throw new Error(`The button from Disclosure ${this._id} is missing its aria-controls attribute.`);
    }

    // Verify that the aria-controls attr is non-empty.
    if (ariaControlsValue === '') {
      throw new Error(`The button from Disclosure ${this._id} has an empty aria-controls attribute.`);
    }

    if (ariaControlsValue) {
      ids = this._button.getAttribute('aria-controls').split(' ');

      this._contents.forEach((content) => {
        const idExists = content.hasAttribute('id');
        const idValue = idExists ? content.getAttribute('id') : '';

        // Verify that this container's id attr exists.
        if (!idExists) {
          throw new Error(`A container from Disclosure ${this._id} is missing its id attribute.`);
        }

        // Verify that this container's id attr is non-empty.
        if (idValue === '') {
          throw new Error(`A container from Disclosure ${this._id} has an empty id attribute.`);
        }

        // Verify that this container's id attr is included in aria-controls.
        if (!ids.includes(idValue)) {
          throw new Error(`Container #${idValue}'s id is not included in Disclosure ${this._id}'s aria-controls attribute.`);
        }
      });
    }
  };

  /**
   * Creates a custom event for the disclosure to dispatch on state changes.
   *
   * Uses default event characteristics, and adds an 'action' parameter to the
   * event's 'detail' property.
   *
   * @return {object}
   *   A custom 'disclosure' event with event.details.action set to the action
   *   just performed: 'expand', 'collapse', 'enable', or 'disable'.
   * @event Disclosure#disclosure
   * @function
   * @protected
   */
  _getEvent = (action) => new CustomEvent(`${this._name}${action}`, {
    bubbles: true,
    cancelable: true,
    composed: false,
  });

  /**
   * Handles click event on button. Private.
   *
   * @param {object} event
   *   The event object passed in by the event listener.
   * @function
   * @protected
   */
  _handleButtonClick = (event) => this.#toggle(!this._expanded);

  /**
   * Determines the default state of the disclosure from the markup.
   *
   * Tests three possibilities:
   *
   *   1. this._button has an aria-expanded attribute set to 'true' (returns
   *      true),
   *   2. this._button has an aria-expanded attribute set to 'false' (returns
   *      false),
   *   3. this._button has no aria-expanded attribute (returns false).
   *
   * @return {boolean}
   *   Whether or this._expanded should be set to true or false when enabling
   *   and disabling the Disclosure.
   * @see disable
   * @see enable
   * @function
   * @protected
   */
  _setDefaultState = () => this._button.hasAttribute('aria-expanded')
    ? this._button.getAttribute('aria-expanded') === 'true'
    : false;

  /**
   * Collapses the disclosure.
   *
   * @see {@link expand}
   * @see {@link #toggle}
   * @function
   * @public
   */
  collapse = () => this.#toggle(false);

  /**
   * Disables the disclosure.
   *
   * @see {@link enable}
   * @function
   * @public
   */
  disable = () => this.#control(false);

  /**
   * Enables the disclosure.
   *
   * @see {@link enable}
   * @function
   * @public
   */
  enable = () => this.#control();

  /**
   * Expands the disclosure.
   *
   * @see {@link collapse}
   * @see {@link #toggle}
   * @function
   * @public
   */
  expand = () => this.#toggle();

  /**
   * Determines if Disclosure is even *available* for use.
   *
   * We do this by checking the button's hidden attribute. If it *is* hidden,
   * the thing is enabled and vice-versa.
   *
   * @return {boolean}
   *   The boolean value of this._enabled.
   * @function
   * @public
   */
  isEnabled = () => this._enabled;

  /**
   * Determines if the Disclosure is currently expanded.
   *
   * We do this by checking two things:
   *
   *   1. the value of the button's aria-expanded attribute, and
   *   2. this.isEnabled()
   *
   * If both are true the Disclosure is expanded; if one or both are false, it
   * is not.
   *
   * In effect this just restricts 'expanded' to enabled Disclosures as far as
   * javascript is concerned, while keeping the *markup* to be simple to manage.
   *
   * @return {boolean}
   *   The boolean value of this._enabled *and* this._expanded.
   * @function
   * @public
   */
  isExpanded = () => this._enabled && this._expanded;

}
